/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * API Client Types - Type definitions for the API client
 */

import type { PaginationInfo } from "@/lib/utils/responses";

// ============================================================================
// CONFIG TYPES
// ============================================================================

export interface ApiClientConfig {
  getToken?: (options?: { template?: string }) => Promise<string | null>;
  getOrganizationId?: () => string | undefined;
  baseUrl?: string;
  autoRetry429?: boolean;
  /** Whether to require authentication token for requests (default: true for POST/PUT/PATCH/DELETE) */
  requireAuth?: boolean;
}

// ============================================================================
// RESULT TYPES
// ============================================================================

export interface ApiSuccess<T> {
  ok: true;
  status: number;
  data: T;
  message?: string;
  requestId?: string;
  pagination?: PaginationInfo;
  metrics?: Record<string, unknown>;
  facets?: Record<string, unknown>;
}

export interface ApiFailure {
  ok: false;
  status: number;
  code?: string;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
  retryAfterSeconds?: number;
}

export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

export interface ApiErrorEnvelope {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

export interface ApiResponseEnvelope<TData = unknown> {
  success?: boolean;
  data?: TData;
  error?: ApiErrorEnvelope;
  message?: string;
  details?: Record<string, unknown>;
  pagination?: PaginationInfo;
  metrics?: Record<string, unknown>;
  facets?: Record<string, unknown>;
}

// ============================================================================
// REQUEST TYPES
// ============================================================================

export interface RequestOptions<TBody = unknown> {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  headers?: Record<string, string>;
  body?: TBody;
  signal?: AbortSignal;
  timeout?: number; // Timeout in milliseconds (default: 30s for external, 5s for internal)
  retry?: {
    /**
     * Whether retries are enabled.
     * Default behavior in `createApiClient`:
     * - `GET`: true
     * - non-`GET`: false
     */
    enabled?: boolean;
    maxAttempts?: number;
    baseDelay?: number;
  };
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isApiFailure<T>(r: ApiResult<T>): r is ApiFailure {
  return !r.ok;
}
